"use client";

import Image from "next/image";
import { useEffect, useRef, useState, type CSSProperties } from "react";

import { CcButton } from "@/components/ui/CcButton";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import { padIndex, type AboutUsHeroCopy } from "./about-us-copy";
import { CcUsLines } from "./CcUsReveal";
import { CcUsScramble, CcUsTypewriter } from "./CcUsText";

type CcUsHeroProps = {
  copy: AboutUsHeroCopy;
  /** Anchor the "Möt teamet" button scrolls to. */
  teamAnchor: string;
};

// Scroll windows (0–1 of the pinned distance) for the title and three chapters.
const STOPS = [0, 0.16, 0.42, 0.7, 1];

const clamp = (value: number) => Math.min(1, Math.max(0, value));
const smooth = (edge0: number, edge1: number, value: number) => {
  const x = clamp((value - edge0) / (edge1 - edge0));
  return x * x * (3 - 2 * x);
};

// A frame with stepped, "pixelated" edges, inset from the photo. Fixed
// pseudo-random depths so server and client draw the same shape.
const STEP_DEPTHS = [2, 0, 1, 2, 1, 0, 2, 1, 0, 1, 2, 0, 1, 2];
type Point = { x: number; y: number; fx: number; fy: number };

function steppedFrame(): Point[] {
  const inset = { x0: 14, x1: 86, y0: 13, y1: 87 };
  const depth = 2.4;
  const n = STEP_DEPTHS.length;
  const points: Point[] = [];
  const scaleX = (x: number) => -2 + ((x - inset.x0) / (inset.x1 - inset.x0)) * 104;
  const scaleY = (y: number) => -2 + ((y - inset.y0) / (inset.y1 - inset.y0)) * 104;
  const push = (x: number, y: number, fx: number, fy: number) => points.push({ x, y, fx, fy });
  for (let i = 0; i < n; i += 1) {
    const d = STEP_DEPTHS[i] * depth;
    const xa = inset.x0 + ((inset.x1 - inset.x0) * i) / n;
    const xb = inset.x0 + ((inset.x1 - inset.x0) * (i + 1)) / n;
    push(xa, inset.y0 + d, scaleX(xa), -2);
    push(xb, inset.y0 + d, scaleX(xb), -2);
  }
  for (let i = 0; i < n; i += 1) {
    const d = STEP_DEPTHS[(i + 5) % n] * depth;
    const ya = inset.y0 + ((inset.y1 - inset.y0) * i) / n;
    const yb = inset.y0 + ((inset.y1 - inset.y0) * (i + 1)) / n;
    push(inset.x1 - d, ya, 102, scaleY(ya));
    push(inset.x1 - d, yb, 102, scaleY(yb));
  }
  for (let i = 0; i < n; i += 1) {
    const d = STEP_DEPTHS[(i + 9) % n] * depth;
    const xa = inset.x1 - ((inset.x1 - inset.x0) * i) / n;
    const xb = inset.x1 - ((inset.x1 - inset.x0) * (i + 1)) / n;
    push(xa, inset.y1 - d, scaleX(xa), 102);
    push(xb, inset.y1 - d, scaleX(xb), 102);
  }
  for (let i = 0; i < n; i += 1) {
    const d = STEP_DEPTHS[(i + 3) % n] * depth;
    const ya = inset.y1 - ((inset.y1 - inset.y0) * i) / n;
    const yb = inset.y1 - ((inset.y1 - inset.y0) * (i + 1)) / n;
    push(inset.x0 + d, ya, -2, scaleY(ya));
    push(inset.x0 + d, yb, -2, scaleY(yb));
  }
  return points;
}

const FRAME = steppedFrame();

/**
 * 0 = full bleed, 1 = the stepped inset frame. `scale` shrinks the shape
 * towards the centre (0 = a point), used to grow the second city out of
 * the middle of the data map.
 */
function framePolygon(amount: number, scale = 1) {
  const lerp = (a: number, b: number) => a + (b - a) * amount;
  const shrink = (value: number) => 50 + (value - 50) * scale;
  return `polygon(${FRAME.map(
    (p) => `${shrink(lerp(p.fx, p.x)).toFixed(2)}% ${shrink(lerp(p.fy, p.y)).toFixed(2)}%`,
  ).join(",")})`;
}

/** Everything the hero shows at scroll progress `p` (0–1 of the pinned distance). */
function heroState(p: number) {
  const chapter = STOPS.findIndex((stop, index) => p >= stop && p < (STOPS[index + 1] ?? 1.01));
  // Chapter 2 (index 2) is the grey data map; 3 restores the colour.
  const digital = smooth(STOPS[2] - 0.04, STOPS[2] + 0.06, p) * (1 - smooth(STOPS[3] - 0.03, STOPS[3] + 0.08, p));
  return {
    chapter,
    digital,
    // Malmö zooms in; Stockholm takes over mid-map, already zoomed, then pulls back.
    zoomA: 1 + 0.26 * smooth(0.08, 0.52, p),
    swap: smooth(0.49, 0.62, p),
    zoomB: 1 + 0.26 * (1 - smooth(0.6, 0.97, p)),
    titleOut: smooth(0.04, STOPS[1], p),
  };
}

/**
 * Pinned two-city hero, modelled on inversa.com. While the visitor scrolls:
 * the title sits over Malmö; chapter 1 zooms in towards Turning Torso;
 * chapter 2 turns it into a grey "data map" inside a stepped frame on a
 * dotted grid, and Stockholm grows out of the middle of the map, zoomed in;
 * chapter 3 brings the colour back and zooms Stockholm out to full bleed.
 * The Malmö photo is the LCP image and is never hidden.
 */
export function CcUsHero({ copy, teamAnchor }: CcUsHeroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const photoARef = useRef<HTMLDivElement>(null);
  const photoBRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLSpanElement>(null);
  const reduced = useCcPrefersReducedMotion();
  // Only the chapter index lives in React state; everything that changes on
  // every scroll frame is written straight to the DOM, so scrolling never
  // re-renders the hero.
  const [chapter, setChapter] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || reduced) return;
    let frame = 0;
    let last = -1;
    const update = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const distance = rect.height - window.innerHeight;
      const p = distance > 0 ? clamp(-rect.top / distance) : 0;
      if (Math.abs(p - last) < 0.0005) return;
      last = p;
      const s = heroState(p);
      const stage = stageRef.current;
      if (stage) {
        stage.dataset.digital = s.digital > 0.001 ? "on" : "off";
      }
      stageRef.current?.style.setProperty("--cc-us-digital", s.digital.toFixed(3));
      stageRef.current?.style.setProperty("--cc-us-title-out", s.titleOut.toFixed(3));
      if (frameRef.current) frameRef.current.style.clipPath = s.digital > 0.001 ? framePolygon(s.digital) : "";
      if (photoARef.current) photoARef.current.style.transform = `scale(${s.zoomA.toFixed(4)})`;
      const next = photoBRef.current;
      if (next) {
        next.style.visibility = s.swap > 0 ? "visible" : "hidden";
        next.style.clipPath = s.swap > 0 && s.swap < 1 ? framePolygon(s.digital, s.swap) : "";
        next.style.transform = `scale(${s.zoomB.toFixed(4)})`;
      }
      if (railRef.current) railRef.current.style.transform = `scaleY(${p.toFixed(3)})`;
      setChapter((current) => (current === s.chapter ? current : s.chapter));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [reduced]);

  // Server and first paint: the title over Malmö (progress 0).
  const start = heroState(0);
  const [malmo, stockholm] = copy.images;

  const stageStyle = {
    "--cc-us-digital": start.digital.toFixed(3),
    "--cc-us-title-out": start.titleOut.toFixed(3),
  } as CSSProperties;

  return (
    <section
      ref={sectionRef}
      className="cc-us-hero"
      aria-labelledby="cc-us-hero-title"
      data-cc-nav="dark"
      {...devSectionAttr("about-hero")}
    >
      <div ref={stageRef} className="cc-us-hero__stage" style={stageStyle}>
        <div className="cc-us-hero__dots" aria-hidden="true" />
        <div ref={frameRef} className="cc-us-hero__frame">
          <div
            ref={photoARef}
            className="cc-us-hero__photo"
            style={{ transform: `scale(${start.zoomA.toFixed(4)})`, transformOrigin: `${malmo.focusX}% ${malmo.focusY}%` }}
          >
            <Image
              src={malmo.src}
              alt={malmo.alt}
              width={malmo.width}
              height={malmo.height}
              sizes="100vw"
              quality={85}
              priority
              fetchPriority="high"
            />
            <Image
              src={malmo.src}
              alt=""
              aria-hidden="true"
              className="cc-us-hero__photo-gray"
              width={malmo.width}
              height={malmo.height}
              sizes="100vw"
              quality={85}
              loading="eager"
              fetchPriority="low"
            />
          </div>
          <div
            ref={photoBRef}
            className="cc-us-hero__photo cc-us-hero__photo--next"
            style={{
              visibility: "hidden",
              transform: `scale(${start.zoomB.toFixed(4)})`,
              transformOrigin: `${stockholm.focusX}% ${stockholm.focusY}%`,
            }}
          >
            <Image
              src={stockholm.src}
              alt={stockholm.alt}
              width={stockholm.width}
              height={stockholm.height}
              sizes="100vw"
              quality={85}
              // Hidden until the swap, so lazy loading would wait too long.
              loading="eager"
              fetchPriority="low"
            />
            <Image
              src={stockholm.src}
              alt=""
              aria-hidden="true"
              className="cc-us-hero__photo-gray"
              width={stockholm.width}
              height={stockholm.height}
              sizes="100vw"
              quality={85}
              // Hidden until the swap, so lazy loading would wait too long.
              loading="eager"
              fetchPriority="low"
            />
          </div>
          <div className="cc-us-hero__grid" aria-hidden="true">
            <span style={{ left: "22%", top: "30%", width: "18%", height: "22%" }} />
            <span style={{ left: "52%", top: "18%", width: "14%", height: "30%" }} />
            <span style={{ left: "60%", top: "58%", width: "20%", height: "16%" }} />
          </div>
        </div>
        <div className="cc-us-hero__shade" aria-hidden="true" />

        <div className="cc-container cc-us-hero__title-wrap">
          <p className="cc-us-label">{copy.label}</p>
          <CcUsLines as="h1" id="cc-us-hero-title" className="cc-us-hero__title" lines={copy.titleLines} />
          <div className="cc-us-hero__actions">
            <CcButton href={`#${teamAnchor}`} label={copy.cta} expand expandCompact />
            <span className="cc-us-hero__hint" aria-hidden="true">
              {copy.scrollHint} ↓
            </span>
          </div>
        </div>

        <div className="cc-container cc-us-hero__chapters">
          {copy.chapters.map((item, index) => {
            const on = reduced || chapter === index + 1;
            return (
              <div
                key={item.label}
                className="cc-us-hero__chapter"
                data-on={on ? "true" : "false"}
                data-side="left"
                // Over the calm water on Malmö, over the sky on Stockholm.
                data-pos={index === 0 ? "low" : index === 2 ? "high" : undefined}
              >
                <p className="cc-us-label">
                  <span aria-hidden="true">● </span>
                  <CcUsScramble text={`${padIndex(index)} · ${item.label}`} play={on} />
                </p>
                <CcUsTypewriter className="cc-us-hero__text" text={item.text} play={on} />
              </div>
            );
          })}
        </div>

        <div className="cc-us-hero__rail" aria-hidden="true">
          <span ref={railRef} style={{ transform: "scaleY(0)" }} />
        </div>
      </div>
    </section>
  );
}
